<template>
  <div class="order-mapper">

    <!-- ─── Tab bar ──────────────────────────────────────────── -->
    <div class="om-tabs">
      <button :class="['om-tab', { active: tab === 'mapping' }]" @click="tab = 'mapping'">
        📋 Mapping Orders
      </button>
      <button :class="['om-tab', { active: tab === 'reconcile' }]" @click="tab = 'reconcile'">
        🔗 Reconcile
      </button>
      <button class="om-refresh" title="Atualizar ordens" @click="refreshOrders">↻</button>
    </div>

    <!-- ══════════════════════════════════════════════════════
         TAB: MAPPING
    ══════════════════════════════════════════════════════ -->
    <div v-if="tab === 'mapping'" class="mapping-layout">

      <!-- Column: Buys -->
      <div class="orders-col box-rounded">
        <h3 class="col-title">📈 Compras <span class="badge">{{ unmappedBuys.length }}</span></h3>
        <div class="orders-scroll">
          <div
            v-for="order in unmappedBuys"
            :key="order.orderId"
            :class="['order-card', 'buy-card', { selected: isSelected(order.orderId), partial: order.partial }]"
            @click="toggleOrder(order)"
          >
            <div class="card-row">
              <span class="card-price">{{ Number(order.execPrice).toLocaleString('pt-BR') }}</span>
              <span class="card-qty">{{ order.executedQty }} BTC</span>
            </div>
            <div class="card-meta">{{ order.excg }} · {{ order.account }}</div>
            <div class="card-time">{{ order.time }}</div>
            <span v-if="order.partial" class="partial-badge" title="Parcialmente mapeada">▲ PARCIAL</span>
          </div>
          <div v-if="unmappedBuys.length === 0" class="empty-msg">Nenhuma compra pendente</div>
        </div>
      </div>

      <!-- Column: Sells -->
      <div class="orders-col box-rounded">
        <h3 class="col-title">📉 Vendas <span class="badge">{{ unmappedSells.length }}</span></h3>
        <div class="orders-scroll">
          <div
            v-for="order in unmappedSells"
            :key="order.orderId"
            :class="['order-card', 'sell-card', { selected: isSelected(order.orderId), partial: order.partial }]"
            @click="toggleOrder(order)"
          >
            <div class="card-row">
              <span class="card-price">{{ Number(order.execPrice).toLocaleString('pt-BR') }}</span>
              <span class="card-qty">{{ order.executedQty }} BTC</span>
            </div>
            <div class="card-meta">{{ order.excg }} · {{ order.account }}</div>
            <div class="card-time">{{ order.time }}</div>
            <span v-if="order.partial" class="partial-badge" title="Parcialmente mapeada">▲ PARCIAL</span>
          </div>
          <div v-if="unmappedSells.length === 0" class="empty-msg">Nenhuma venda pendente</div>
        </div>
      </div>

      <!-- Column: Job Builder -->
      <div class="job-builder box-rounded">
        <div v-if="selectedOrders.length === 0" class="empty-msg centered">
          ← Selecione uma ou mais ordens para mapear
        </div>

        <template v-else>
          <div v-for="order in selectedOrders" :key="order.orderId" class="job-card">

            <!-- Job header -->
            <div class="job-header">
              <span :class="['side-badge', order.side === 'buy' ? 'buy' : 'sell']">
                {{ order.side === 'buy' ? '📈 BUY' : '📉 SELL' }}
              </span>
              <span class="job-price">{{ Number(order.execPrice).toLocaleString('pt-BR') }}</span>
              <span class="job-qty-label">{{ order.executedQty }} BTC</span>
              <span class="job-exchange">{{ order.excg }} · {{ order.account }}</span>
            </div>

            <!-- Qty progress bar -->
            <div class="qty-track">
              <div class="qty-bar-wrap">
                <div
                  class="qty-bar-fill"
                  :style="{ width: allocationPct(order) + '%', background: allocationColor(order) }"
                ></div>
              </div>
              <div class="qty-track-label">
                <span>{{ totalLegsQty(order.orderId).toFixed(6) }} / {{ order.executedQty }} alocado</span>
                <span v-if="orderRemaining(order) > 1e-8" class="remaining-hint">
                  ({{ orderRemaining(order).toFixed(6) }} ficará pendente)
                </span>
                <span v-else-if="totalLegsQty(order.orderId) > parseFloat(order.executedQty) + 1e-8" class="over-hint">
                  ⚠ excede qty!
                </span>
              </div>
            </div>

            <!-- Legs list -->
            <div class="legs-list">
              <div v-if="!legs[order.orderId] || legs[order.orderId].length === 0" class="no-legs-hint">
                Sem legs definidas — ordem ficará pendente ao enviar
              </div>

              <div v-for="(leg, li) in (legs[order.orderId] || [])" :key="li" class="leg-item">

                <!-- Top row: action + qty + remove -->
                <div class="leg-top-row">
                  <select v-model="leg.action" class="leg-select" @change="onLegActionChange(order.orderId, li)">
                    <option value="" disabled>-- ação --</option>
                    <template v-if="order.side === 'buy'">
                      <option value="openLong">📈 Open Long</option>
                      <option value="closeShort">🔒 Close Short</option>
                    </template>
                    <template v-else>
                      <option value="closeLong">📉 Close Long</option>
                      <option value="openShort">📤 Open Short</option>
                    </template>
                  </select>

                  <input
                    type="number"
                    v-model.number="leg.qty"
                    :max="order.executedQty"
                    min="0"
                    step="0.000001"
                    class="leg-qty"
                    placeholder="qty"
                  />

                  <button class="leg-del" title="Remover leg" @click="removeLeg(order.orderId, li)">✕</button>
                </div>

                <!-- Params: closeShort -->
                <div v-if="leg.action === 'closeShort'" class="leg-params">
                  <label class="param-label">Short unit</label>
                  <select v-model="leg.shortUnitID" class="param-select full-width">
                    <option value="">-- selecionar short unit --</option>
                    <option v-for="{ id, unit } in sortedActiveShortUnits" :key="id" :value="id">
                      {{ id.substring(0, 18) }}… | entry: {{ unit.origin.sellPrice }} | rem: {{ unit.remainingQty }}
                    </option>
                  </select>
                  <div
                    v-if="leg.shortUnitID"
                    :class="['coverage-bar', closeShortCoverageClass(leg)]"
                  >
                    Rem: {{ closeShortRemaining(leg).toFixed(6) }}
                    / {{ (parseFloat(leg.qty) || 0).toFixed(6) }}
                  </div>
                </div>

                <!-- Params: openShort -->
                <div v-if="leg.action === 'openShort'" class="leg-params grid-2">
                  <label class="param-label">Instrumento</label>
                  <input type="text" v-model="leg.instrument" placeholder="ex: BTC-USDT perp" class="param-input" />
                  <label class="param-label">Borrow Rate</label>
                  <input type="number" v-model.number="leg.borrowRate" step="0.00001" placeholder="0" class="param-input" />
                  <label class="param-label">Borrow Ref</label>
                  <input type="text" v-model="leg.borrowRef" placeholder="referência" class="param-input" />
                  <label class="param-label">Fees</label>
                  <input type="number" v-model.number="leg.fees" step="0.01" placeholder="0" class="param-input" />
                </div>

                <!-- Params: closeLong — unit chips -->
                <div v-if="leg.action === 'closeLong'" class="leg-params">
                  <div :class="['coverage-bar', legCoverageClass(order.orderId, li)]">
                    Cobre: {{ legSellCoverage(order.orderId, li).toFixed(6) }}
                    / {{ (parseFloat(leg.qty) || 0).toFixed(6) }}
                  </div>
                  <div class="unit-chips-wrap">
                    <span
                      v-for="unit in sellableUnits(order)"
                      :key="unit.unitID"
                      :class="['unit-chip', { active: leg.unitsToUse && leg.unitsToUse.includes(unit.unitID), dust: unit.isDust }]"
                      :title="unit.unitID"
                      @click="toggleLegUnit(order.orderId, li, unit.unitID)"
                    >
                      <span class="chip-price">{{ Number(unit.current.avgPrice).toFixed(0) }}</span>
                      <span class="chip-qty">{{ Number(unit.current.qty).toFixed(5) }}</span>
                    </span>
                    <span v-if="sellableUnits(order).length === 0" class="empty-msg">
                      Sem units abaixo de {{ order.execPrice }}
                    </span>
                  </div>
                </div>

              </div>
            </div>

            <!-- Add leg -->
            <button class="add-leg-btn" @click="addLeg(order)">+ Leg</button>

          </div>

          <!-- Send panel -->
          <div class="send-panel">
            <div v-if="globalErrors.length > 0" class="validation-errors">
              <p v-for="(e, i) in globalErrors" :key="i">{{ e }}</p>
            </div>
            <div class="send-row">
              <button class="clear-btn" @click="clearSelection">Limpar</button>
              <button class="send-btn" :disabled="!canSend" @click="sendOrders">Enviar</button>
            </div>
          </div>
        </template>
      </div>

    </div>

    <!-- ══════════════════════════════════════════════════════
         TAB: RECONCILE
    ══════════════════════════════════════════════════════ -->
    <div v-if="tab === 'reconcile'" class="reconcile-wrapper">

      <div class="mode-toggle">
        <button :class="['mode-btn', { active: reconcileMode === 'shortFirst' }]" @click="setReconcileMode('shortFirst')">
          Short → Long
        </button>
        <button :class="['mode-btn', { active: reconcileMode === 'spotFirst' }]" @click="setReconcileMode('spotFirst')">
          Long → Short
        </button>
      </div>

      <!-- shortFirst: select a short, allocate spot units against it -->
      <div v-if="reconcileMode === 'shortFirst'" class="reconcile-grid">

        <div class="box-rounded">
          <h3 class="col-title">📉 Shorts Ativos <span class="badge">{{ sortedActiveShortUnits.length }}</span></h3>
          <div class="cards-scroll">
            <div v-if="sortedActiveShortUnits.length === 0" class="empty-msg">Nenhum short ativo.</div>
            <div
              v-for="{ id, unit } in sortedActiveShortUnits"
              :key="id"
              :class="['order-card', { selected: selectedShortID === id }]"
              @click="selectedShortID = id"
            >
              <div class="card-row">
                <span class="card-price">{{ unit.origin.sellPrice }}</span>
                <span class="card-qty">{{ unit.remainingQty }}</span>
              </div>
              <div class="card-meta">{{ unit.instrument }} | {{ id.substring(0, 18) }}…</div>
            </div>
          </div>
        </div>

        <div class="box-rounded">
          <h3 class="col-title">📈 Alocar Shorts para Longs</h3>
          <div v-if="!selectedShortID" class="empty-msg">Selecione um short à esquerda.</div>
          <template v-else>

            <div class="reconcile-info">
              <span>Short: <strong>{{ selectedShortID.substring(0, 18) }}…</strong></span>
              <span>Remaining: <strong>{{ selectedShort.remainingQty }}</strong></span>
              <span>Alocado:
                <strong :style="{ color: totalAllocQty > selectedShort.remainingQty ? '#ef4444' : '#10b981' }">
                  {{ totalAllocQty.toFixed(6) }}
                </strong>
              </span>
            </div>

            <div class="chips-grid">
              <div
                v-for="unit in pendingManualUnits"
                :key="unit.unitID"
                :class="['spot-chip', { allocated: spotAllocations[unit.unitID] !== undefined }]"
                @click="toggleSpotUnit(unit.unitID)"
              >
                <div class="chip-id">{{ unit.unitID.substring(0, 16) }}…</div>
                <div class="chip-sub">
                  {{ Number(unit.current.avgPrice).toFixed(0) }} | {{ Number(unit.current.qty).toFixed(6) }}
                </div>
                <div v-if="spotAllocations[unit.unitID] !== undefined" class="chip-qty-input" @click.stop>
                  <input
                    type="number"
                    v-model.number="spotAllocations[unit.unitID]"
                    :max="unit.current.qty"
                    min="0"
                    step="0.000001"
                    class="qty-input"
                  />
                </div>
              </div>
              <div v-if="pendingManualUnits.length === 0" class="empty-msg">Nenhuma spot unit disponível.</div>
            </div>

            <div class="params-row">
              <label class="small-label">Fees</label>
              <input type="number" v-model.number="reconcileFees" step="0.01" class="param-input" />
              <label class="small-label">Interest Paid</label>
              <input type="number" v-model.number="reconcileInterest" step="0.01" class="param-input" />
            </div>

            <div :class="['pnl-preview', reconcilePnL >= 0 ? 'positive' : 'negative']">
              PnL Estimado: {{ reconcilePnL.toFixed(2) }} USD
            </div>

            <div v-if="reconcileErrors.length > 0" class="validation-errors">
              <p v-for="(e, i) in reconcileErrors" :key="i">{{ e }}</p>
            </div>

            <button class="send-btn full-width" :disabled="!canReconcile" @click="sendReconcile">
              Reconciliar
            </button>

          </template>
        </div>

      </div>

      <!-- spotFirst: select a spot unit, allocate against N shorts -->
      <div v-else class="reconcile-grid">

        <div class="box-rounded">
          <h3 class="col-title">📈 Spot Units <span class="badge">{{ pendingManualUnits.length }}</span></h3>
          <div class="cards-scroll">
            <div v-if="pendingManualUnits.length === 0" class="empty-msg">Nenhuma spot unit disponível.</div>
            <div
              v-for="unit in pendingManualUnits"
              :key="unit.unitID"
              :class="['order-card', { selected: selectedSpotID === unit.unitID }]"
              @click="selectedSpotID = unit.unitID"
            >
              <div class="card-row">
                <span class="card-price">{{ Number(unit.current.avgPrice).toFixed(0) }}</span>
                <span class="card-qty">{{ Number(unit.current.qty).toFixed(6) }}</span>
              </div>
              <div class="card-meta">{{ unit.unitID.substring(0, 18) }}…</div>
            </div>
          </div>
        </div>

        <div class="box-rounded">
          <h3 class="col-title">📉 Alocar Longs para Shorts</h3>
          <div v-if="!selectedSpotID" class="empty-msg">Selecione uma spot unit à esquerda.</div>
          <template v-else>

            <div class="reconcile-info">
              <span>Spot: <strong>{{ selectedSpotID.substring(0, 18) }}…</strong></span>
              <span>Disponível: <strong>{{ selectedSpot ? Number(selectedSpot.current.qty).toFixed(6) : 0 }}</strong></span>
              <span>Alocado:
                <strong :style="{ color: totalSpotConsumed > (selectedSpot ? parseFloat(selectedSpot.current.qty) : 0) ? '#ef4444' : '#10b981' }">
                  {{ totalSpotConsumed.toFixed(6) }}
                </strong>
              </span>
            </div>

            <div class="chips-grid">
              <div v-if="sortedActiveShortUnits.length === 0" class="empty-msg">Nenhum short ativo.</div>
              <div
                v-for="{ id, unit } in sortedActiveShortUnits"
                :key="id"
                :class="['spot-chip', { allocated: shortAllocations[id] !== undefined }]"
                @click="toggleShortUnit(id)"
              >
                <div class="chip-id">{{ id.substring(0, 16) }}… — {{ unit.instrument }}</div>
                <div class="chip-sub">Entry: {{ unit.origin.sellPrice }} | Rem: {{ unit.remainingQty }}</div>
                <div v-if="shortAllocations[id] !== undefined" class="chip-qty-input" @click.stop>
                  <input
                    type="number"
                    v-model.number="shortAllocations[id]"
                    :max="Math.min(unit.remainingQty, selectedSpot ? parseFloat(selectedSpot.current.qty) : 0)"
                    min="0"
                    step="0.000001"
                    class="qty-input"
                  />
                </div>
              </div>
            </div>

            <div class="params-row">
              <label class="small-label">Fees</label>
              <input type="number" v-model.number="reconcileFees" step="0.01" class="param-input" />
              <label class="small-label">Interest Paid</label>
              <input type="number" v-model.number="reconcileInterest" step="0.01" class="param-input" />
            </div>

            <div :class="['pnl-preview', spotFirstPnL >= 0 ? 'positive' : 'negative']">
              PnL Estimado: {{ spotFirstPnL.toFixed(2) }} USD
            </div>

            <div v-if="spotFirstErrors.length > 0" class="validation-errors">
              <p v-for="(e, i) in spotFirstErrors" :key="i">{{ e }}</p>
            </div>

            <button class="send-btn full-width" :disabled="!canSpotFirstReconcile" @click="sendReconcile">
              Reconciliar
            </button>

          </template>
        </div>

      </div>

    </div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'OrderMapper',

  data() {
    return {
      tab: 'mapping',

      // ── Mapping state ──────────────────────────────────────────
      selectedOrders: [],  // [order object, ...]
      legs: {},            // { [orderId]: [{ action, qty, ...params }] }

      // ── Reconcile state ────────────────────────────────────────
      reconcileMode:     'shortFirst',
      selectedShortID:   null,
      spotAllocations:   {},    // { [unitID]: qty }   (shortFirst)
      selectedSpotID:    null,
      shortAllocations:  {},    // { [shortUnitID]: qty } (spotFirst)
      reconcileFees:     0,
      reconcileInterest: 0,
    };
  },

  computed: {
    ...mapGetters(['unMappedManuals', 'manualUnits', 'dustRegularUnits']),

    unmappedBuys()  { return (this.unMappedManuals?.buys  || []).map(o => ({ ...o, side: o.side?.toLowerCase() })); },
    unmappedSells() { return (this.unMappedManuals?.sells || []).map(o => ({ ...o, side: o.side?.toLowerCase() })); },

    activeShortUnits() {
      const sl = this.$store.state.shortLedger || {};
      return Object.fromEntries(Object.entries(sl).filter(([, u]) => u.status === 'active'));
    },

    sortedActiveShortUnits() {
      return Object.entries(this.activeShortUnits)
        .sort(([, a], [, b]) => b.origin.sellPrice - a.origin.sellPrice)
        .map(([id, unit]) => ({ id, unit }));
    },

    pendingManualUnits() {
      const ledger = this.$store.state.ledger || {};
      return Object.values(ledger)
        .filter(u => u.bucket === 'manual' && u.status === 'pending' && parseFloat(u.current?.qty || 0) > 0)
        .sort((a, b) => a.current.avgPrice - b.current.avgPrice);
    },

    // ── Mapping computed ──────────────────────────────────────
    globalErrors() {
      const errors = [];
      const consumedByShort = {};
      for (const order of this.selectedOrders) {
        const execQty = parseFloat(order.executedQty);
        const total   = this.totalLegsQty(order.orderId);

        if (total > execQty + 1e-8) {
          errors.push(`Ordem ${order.orderId.substring(0, 12)}: legs excedem qty (${total.toFixed(6)} > ${execQty})`);
        }

        for (const [li, leg] of (this.legs[order.orderId] || []).entries()) {
          if (!leg.action) {
            errors.push(`Ordem ${order.orderId.substring(0, 12)}: leg ${li + 1} sem ação.`);
          }
          if (leg.action === 'closeShort') {
            if (!leg.shortUnitID) {
              errors.push(`Leg ${li + 1} closeShort: selecione uma short unit.`);
            } else {
              const needed    = parseFloat(leg.qty) || 0;
              const remaining = this.closeShortRemaining(leg);
              const already   = consumedByShort[leg.shortUnitID] || 0;
              const available = remaining - already;
              if (needed > available + 1e-8) {
                errors.push(`Leg ${li + 1} closeShort: qty excede remainingQty (${needed.toFixed(6)} > ${available.toFixed(6)}).`);
              }
              consumedByShort[leg.shortUnitID] = already + needed;
            }
          }
          if (leg.action === 'openShort' && !leg.instrument) {
            errors.push(`Leg ${li + 1} openShort: instrumento obrigatório.`);
          }
          if (leg.action === 'closeLong') {
            const cov    = this.legSellCoverage(order.orderId, li);
            const needed = parseFloat(leg.qty) || 0;
            if (needed > 0 && cov < needed - 1e-8) {
              errors.push(`Leg ${li + 1} closeLong: units insuficientes (${cov.toFixed(6)} < ${needed.toFixed(6)}).`);
            }
          }
        }
      }
      return errors;
    },

    canSend() {
      if (this.selectedOrders.length === 0) return false;
      if (this.globalErrors.length > 0) return false;
      return this.selectedOrders.some(o => (this.legs[o.orderId] || []).length > 0);
    },

    // ── Reconcile shortFirst ──────────────────────────────────
    selectedShort() {
      return this.selectedShortID ? (this.activeShortUnits[this.selectedShortID] || null) : null;
    },

    totalAllocQty() {
      return Object.values(this.spotAllocations).reduce((s, q) => s + (parseFloat(q) || 0), 0);
    },

    reconcilePnL() {
      if (!this.selectedShort) return 0;
      const sp = this.selectedShort.origin.sellPrice;
      let total = 0;
      for (const [uid, qty] of Object.entries(this.spotAllocations)) {
        const unit = this.pendingManualUnits.find(u => u.unitID === uid);
        if (!unit || !(parseFloat(qty) > 0)) continue;
        total += (sp - unit.current.avgPrice) * parseFloat(qty);
      }
      return total - this.reconcileFees - this.reconcileInterest;
    },

    reconcileErrors() {
      const errors = [];
      if (!this.selectedShortID)   errors.push('Selecione um short unit.');
      if (this.totalAllocQty <= 0) errors.push('Aloque pelo menos uma spot unit.');
      if (this.selectedShort && this.totalAllocQty > this.selectedShort.remainingQty)
        errors.push(`Qty total excede remainingQty (${this.selectedShort.remainingQty}).`);
      for (const [uid, qty] of Object.entries(this.spotAllocations)) {
        const unit = this.pendingManualUnits.find(u => u.unitID === uid);
        if (unit && parseFloat(qty) > parseFloat(unit.current.qty))
          errors.push(`${uid.substring(0, 14)}: excede disponível (${unit.current.qty}).`);
      }
      return errors;
    },

    canReconcile() {
      return this.reconcileErrors.length === 0 && this.totalAllocQty > 0;
    },

    // ── Reconcile spotFirst ───────────────────────────────────
    selectedSpot() {
      return this.selectedSpotID
        ? (this.pendingManualUnits.find(u => u.unitID === this.selectedSpotID) || null)
        : null;
    },

    totalSpotConsumed() {
      return Object.values(this.shortAllocations).reduce((s, q) => s + (parseFloat(q) || 0), 0);
    },

    spotFirstPnL() {
      if (!this.selectedSpot) return 0;
      const spotAvg = parseFloat(this.selectedSpot.current.avgPrice);
      let total = 0;
      for (const [sid, qty] of Object.entries(this.shortAllocations)) {
        const short = this.activeShortUnits[sid];
        if (!short || !(parseFloat(qty) > 0)) continue;
        total += (short.origin.sellPrice - spotAvg) * parseFloat(qty);
      }
      return total - this.reconcileFees - this.reconcileInterest;
    },

    spotFirstErrors() {
      const errors = [];
      if (!this.selectedSpotID)           errors.push('Selecione uma spot unit.');
      if (this.totalSpotConsumed <= 0)    errors.push('Aloque contra pelo menos um short.');
      if (this.selectedSpot && this.totalSpotConsumed > parseFloat(this.selectedSpot.current.qty))
        errors.push(`Qty consumida excede spot disponível (${this.selectedSpot.current.qty}).`);
      for (const [sid, qty] of Object.entries(this.shortAllocations)) {
        const short = this.activeShortUnits[sid];
        if (short && parseFloat(qty) > short.remainingQty)
          errors.push(`${sid.substring(0, 12)}: excede remainingQty (${short.remainingQty}).`);
      }
      return errors;
    },

    canSpotFirstReconcile() {
      return this.spotFirstErrors.length === 0 && this.totalSpotConsumed > 0;
    },
  },

  watch: {
    selectedShortID() { this.spotAllocations = {}; },
    selectedSpotID()  { this.shortAllocations = {}; },
  },

  methods: {
    // ── Mapping helpers ──────────────────────────────────────────

    isSelected(orderId) {
      return this.selectedOrders.some(o => o.orderId === orderId);
    },

    toggleOrder(order) {
      const idx = this.selectedOrders.findIndex(o => o.orderId === order.orderId);
      if (idx === -1) {
        this.selectedOrders = [...this.selectedOrders, order];
        if (!this.legs[order.orderId]) {
          const defaultAction = order.side === 'buy' ? 'openLong' : 'closeLong';
          this.legs[order.orderId] = [
            this._newLeg(order, defaultAction, parseFloat(order.executedQty)),
          ];
        }
      } else {
        this.selectedOrders = this.selectedOrders.filter(o => o.orderId !== order.orderId);
        delete this.legs[order.orderId];
      }
    },

    _newLeg(order, action, qty = 0) {
      const base = { action, qty };
      if (action === 'closeShort') return { ...base, shortUnitID: '' };
      if (action === 'openShort')  return { ...base, instrument: '', borrowRef: '', borrowRate: 0, fees: 0 };
      if (action === 'closeLong')   return { ...base, unitsToUse: [] };
      return base; // openLong — no extra params
    },

    addLeg(order) {
      const defaultAction = order.side === 'buy' ? 'openLong' : 'closeLong';
      const existing = this.legs[order.orderId] || [];
      this.legs[order.orderId] = [...existing, this._newLeg(order, defaultAction)];
    },

    removeLeg(orderId, li) {
      const updated = [...this.legs[orderId]];
      updated.splice(li, 1);
      this.legs[orderId] = updated;
    },

    onLegActionChange(orderId, li) {
      const order = this.selectedOrders.find(o => o.orderId === orderId);
      if (!order) return;
      const currentQty = this.legs[orderId][li].qty;
      const rebuilt    = this._newLeg(order, this.legs[orderId][li].action, currentQty);
      const updated    = [...this.legs[orderId]];
      updated[li]      = rebuilt;
      this.legs[orderId] = updated;
    },

    totalLegsQty(orderId) {
      return (this.legs[orderId] || []).reduce((s, l) => s + (parseFloat(l.qty) || 0), 0);
    },

    orderRemaining(order) {
      return Math.max(0, parseFloat(order.executedQty) - this.totalLegsQty(order.orderId));
    },

    allocationPct(order) {
      const total = parseFloat(order.executedQty);
      if (!total) return 0;
      return Math.min(100, (this.totalLegsQty(order.orderId) / total) * 100);
    },

    allocationColor(order) {
      if (this.totalLegsQty(order.orderId) > parseFloat(order.executedQty) + 1e-8) return '#ef4444';
      if (this.allocationPct(order) >= 100) return '#10b981';
      return '#6366f1';
    },

    // Units eligible for a closeLong leg: avgPrice < order execPrice and qty > 0
    sellableUnits(order) {
      const sellPrice = parseFloat(order.execPrice);
      const dust      = this.dustRegularUnits.map(u => ({ ...u, isDust: true }));
      return [...this.manualUnits, ...dust]
        .filter(u => parseFloat(u.current?.avgPrice || 0) < sellPrice && parseFloat(u.current?.qty || 0) > 0)
        .sort((a, b) => {
          if (a.isDust !== b.isDust) return a.isDust ? -1 : 1;
          return parseFloat(a.current.avgPrice) - parseFloat(b.current.avgPrice);
        });
    },

    closeShortRemaining(leg) {
      const short = this.activeShortUnits[leg.shortUnitID];
      return parseFloat(short?.remainingQty || 0);
    },

    closeShortCoverageClass(leg) {
      const rem    = this.closeShortRemaining(leg);
      const needed = parseFloat(leg.qty) || 0;
      if (needed <= 0) return '';
      if (needed <= rem + 1e-8) return 'ok';
      if (rem > 0)              return 'partial';
      return 'empty';
    },

    // Sum of available qty for units already picked in a closeLong leg
    legSellCoverage(orderId, li) {
      const leg = (this.legs[orderId] || [])[li];
      if (!leg || leg.action !== 'closeLong') return 0;
      const allUnits = [...this.manualUnits, ...this.dustRegularUnits];
      return (leg.unitsToUse || []).reduce((sum, uid) => {
        const unit = allUnits.find(u => u.unitID === uid);
        return sum + parseFloat(unit?.current?.qty || 0);
      }, 0);
    },

    legCoverageClass(orderId, li) {
      const leg    = (this.legs[orderId] || [])[li];
      if (!leg) return '';
      const cov    = this.legSellCoverage(orderId, li);
      const needed = parseFloat(leg.qty) || 0;
      if (needed <= 0) return '';
      if (cov >= needed - 1e-8) return 'ok';
      if (cov > 0)              return 'partial';
      return 'empty';
    },

    toggleLegUnit(orderId, li, unitID) {
      const orderLegs = [...this.legs[orderId]];
      const leg       = { ...orderLegs[li] };
      const arr       = leg.unitsToUse ? [...leg.unitsToUse] : [];
      const idx       = arr.indexOf(unitID);
      if (idx === -1) arr.push(unitID);
      else            arr.splice(idx, 1);
      leg.unitsToUse  = arr;
      orderLegs[li]   = leg;
      this.legs[orderId] = orderLegs;
    },

    clearSelection() {
      this.selectedOrders = [];
      this.legs = {};
    },

    sendOrders() {
      if (!this.canSend) return;

      const orders = this.selectedOrders
        .filter(order => (this.legs[order.orderId] || []).length > 0)
        .map(order => ({
          excgID:      order.orderId,
          price:       order.execPrice,
          exchange:    order.excg,
          excgAccount: order.account,
          executedQty: order.executedQty,
          originalQty: order.originalQty ?? order.executedQty,
          legs: (this.legs[order.orderId] || []).map(leg => {
            const base = { action: leg.action, qty: leg.qty };
            if (leg.action === 'openLong')    return base;
            if (leg.action === 'closeLong')   return { ...base, unitsToUse: leg.unitsToUse || [] };
            if (leg.action === 'openShort')  return { ...base, instrument: leg.instrument, borrowRef: leg.borrowRef || '', borrowRate: leg.borrowRate || 0, fees: leg.fees || 0 };
            if (leg.action === 'closeShort') return { ...base, shortUnitID: leg.shortUnitID, fees: 0, interestPaid: 0 };
            return base;
          }),
        }));

      const payload = { type: 'batchMapOrders', orders };
      console.log('[OrderMapper] Enviando batchMapOrders:', payload);
      this.$store.state.socket.send(JSON.stringify(payload));
      this.clearSelection();
    },

    refreshOrders() {
      this.$store.state.socket.send(JSON.stringify({ type: 'getManualOrders' }));
    },

    // ── Reconcile methods ────────────────────────────────────────

    setReconcileMode(mode) {
      this.reconcileMode = mode;
      this._resetReconcile();
    },

    _resetReconcile() {
      this.selectedShortID   = null;
      this.spotAllocations   = {};
      this.selectedSpotID    = null;
      this.shortAllocations  = {};
      this.reconcileFees     = 0;
      this.reconcileInterest = 0;
    },

    toggleSpotUnit(unitID) {
      if (this.spotAllocations[unitID] !== undefined) {
        const a = { ...this.spotAllocations };
        delete a[unitID];
        this.spotAllocations = a;
      } else {
        const unit = this.pendingManualUnits.find(u => u.unitID === unitID);
        if (unit && this.selectedShort) {
          const remainingShort = parseFloat(this.selectedShort.remainingQty) - this.totalAllocQty;
          const defaultQty     = Math.max(0, Math.min(parseFloat(unit.current.qty), remainingShort));
          this.spotAllocations = { ...this.spotAllocations, [unitID]: defaultQty };
        }
      }
    },

    toggleShortUnit(shortUnitID) {
      if (this.shortAllocations[shortUnitID] !== undefined) {
        const a = { ...this.shortAllocations };
        delete a[shortUnitID];
        this.shortAllocations = a;
      } else {
        const short = this.activeShortUnits[shortUnitID];
        if (short && this.selectedSpot) {
          const remainingSpot = parseFloat(this.selectedSpot.current.qty) - this.totalSpotConsumed;
          const defaultQty    = Math.max(0, Math.min(parseFloat(short.remainingQty), remainingSpot));
          this.shortAllocations = { ...this.shortAllocations, [shortUnitID]: defaultQty };
        }
      }
    },

    sendReconcile() {
      let payload;
      const purpose = this.reconcileMode === 'shortFirst' ? 'closeShort' : 'closeLong';

      if (this.reconcileMode === 'shortFirst') {
        if (!this.canReconcile) return;
        const spotAllocations = Object.entries(this.spotAllocations)
          .filter(([, qty]) => parseFloat(qty) > 0)
          .map(([spotUnitID, qty]) => ({ spotUnitID, qty: parseFloat(qty) }));
        payload = {
          type: 'batchReconcileSpotShort',
          orders: [{ shortUnitID: this.selectedShortID, spotAllocations, fees: this.reconcileFees, interestPaid: this.reconcileInterest, purpose }],
        };
      } else {
        if (!this.canSpotFirstReconcile) return;
        const entries  = Object.entries(this.shortAllocations).filter(([, qty]) => parseFloat(qty) > 0);
        const totalQty = entries.reduce((s, [, q]) => s + parseFloat(q), 0);
        const orders   = entries.map(([shortUnitID, qty]) => {
          const fraction = parseFloat(qty) / totalQty;
          return {
            shortUnitID,
            spotAllocations: [{ spotUnitID: this.selectedSpotID, qty: parseFloat(qty) }],
            fees:            parseFloat((fraction * this.reconcileFees).toFixed(8)),
            interestPaid:    parseFloat((fraction * this.reconcileInterest).toFixed(8)),
            purpose,
          };
        });
        payload = { type: 'batchReconcileSpotShort', orders };
      }

      console.log('[OrderMapper] Enviando batchReconcileSpotShort:', payload);
      this.$store.state.socket.send(JSON.stringify(payload));
      this._resetReconcile();
    },
  },
};
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────────────── */
.order-mapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.om-tabs {
  display: flex;
  gap: 8px;
  align-items: center;
}

.om-tab {
  padding: 6px 18px;
  border-radius: 8px;
  border: 1px solid #4b5563;
  background: var(--table-row-bgcolor);
  color: var(--text-muted, #9ca3af);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.om-tab.active {
  border-color: #6366f1;
  background: #1e1e2e;
  color: #a5b4fc;
  font-weight: 600;
}

.om-refresh {
  margin-left: auto;
  background: transparent;
  border: 1px solid #4b5563;
  border-radius: 6px;
  color: var(--text-muted, #9ca3af);
  padding: 4px 10px;
  cursor: pointer;
  font-size: 16px;
  transition: color 0.2s;
}
.om-refresh:hover { color: var(--text-color); }

/* ── Mapping layout: 3 columns ───────────────────────────────── */
.mapping-layout {
  display: grid;
  grid-template-columns: 1fr 1fr 3fr;
  gap: 12px;
  min-height: 500px;
}

.orders-col {
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

.orders-scroll {
  overflow-y: auto;
  flex: 1;
  max-height: 520px;
}

/* ── Order cards ─────────────────────────────────────────────── */
.order-card {
  background: var(--table-row-bgcolor);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
  cursor: pointer;
  border-left: 4px solid #6b7280;
  transition: all 0.2s;
  position: relative;
}
.order-card:hover     { background: var(--table-row-hover); transform: translateY(-1px); }
.order-card.selected  { border-left-color: #6366f1; background: #1e1e2e; }
.buy-card             { border-left-color: #10b981; }
.sell-card            { border-left-color: #ef4444; }
.order-card.partial   { opacity: 0.85; }

.card-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 3px;
}
.card-price { color: var(--text-color); }
.card-qty   { color: #a5b4fc; }
.card-meta  { font-size: 11px; color: var(--text-muted, #9ca3af); margin-bottom: 2px; }
.card-time  { font-size: 10px; color: var(--text-muted, #6b7280); }

.partial-badge {
  position: absolute;
  top: 6px;
  right: 8px;
  background: #f59e0b;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 5px;
  border-radius: 4px;
}

/* ── Job Builder ─────────────────────────────────────────────── */
.job-builder {
  overflow-y: auto;
  max-height: 600px;
  padding: 12px;
}

.job-card {
  border: 1px solid #374151;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 14px;
  background: var(--table-row-bgcolor);
}

.job-header {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.side-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
}
.side-badge.buy  { background: #064e3b44; color: #10b981; }
.side-badge.sell { background: #7f1d1d44; color: #ef4444; }

.job-price        { font-weight: 600; font-size: 14px; }
.job-qty-label    { font-size: 12px; color: #a5b4fc; }
.job-exchange     { font-size: 11px; color: var(--text-muted, #9ca3af); margin-left: auto; }

/* ── Qty progress ────────────────────────────────────────────── */
.qty-track { margin-bottom: 10px; }

.qty-bar-wrap {
  height: 6px;
  background: #374151;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 4px;
}
.qty-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s, background 0.3s;
}
.qty-track-label { font-size: 11px; color: var(--text-muted, #9ca3af); display: flex; gap: 10px; }
.remaining-hint  { color: #f59e0b; }
.over-hint       { color: #ef4444; font-weight: 600; }

/* ── Legs ────────────────────────────────────────────────────── */
.legs-list { margin-bottom: 8px; }

.no-legs-hint {
  font-size: 12px;
  color: var(--text-muted, #6b7280);
  font-style: italic;
  padding: 6px 0;
}

.leg-item {
  border-top: 1px solid #374151;
  padding: 8px 0;
}

.leg-top-row {
  display: flex;
  gap: 6px;
  align-items: center;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.leg-select {
  padding: 4px 6px;
  border-radius: 4px;
  border: 1px solid #4b5563;
  background: var(--table-row-bgcolor);
  color: var(--text-color);
  font-size: 12px;
  min-width: 140px;
}

.leg-qty {
  width: 110px;
  padding: 4px 6px;
  border-radius: 4px;
  border: 1px solid #4b5563;
  background: var(--table-row-bgcolor);
  color: var(--text-color);
  font-size: 12px;
}

.leg-del {
  margin-left: auto;
  background: #7f1d1d44;
  color: #ef4444;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 11px;
}
.leg-del:hover { background: #ef4444; color: #fff; }

/* ── Leg params ──────────────────────────────────────────────── */
.leg-params {
  padding: 6px 0 4px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.leg-params.grid-2 {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 8px;
  align-items: center;
}

.param-label  { font-size: 11px; font-weight: 600; color: var(--text-muted, #9ca3af); white-space: nowrap; }
.param-input  { padding: 4px 6px; border-radius: 4px; border: 1px solid #4b5563; background: var(--table-row-bgcolor); color: var(--text-color); font-size: 12px; }
.param-select { padding: 4px 6px; border-radius: 4px; border: 1px solid #4b5563; background: var(--table-row-bgcolor); color: var(--text-color); font-size: 12px; }
.full-width   { width: 100%; }

/* ── Coverage bar (closeLong) ─────────────────────────────────── */
.coverage-bar {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: 600;
  margin-bottom: 4px;
  display: inline-block;
}
.coverage-bar.ok      { background: #064e3b44; color: #10b981; }
.coverage-bar.partial { background: #78350f44; color: #f59e0b; }
.coverage-bar.empty   { background: #7f1d1d44; color: #ef4444; }

/* ── Unit chips (closeLong) ───────────────────────────────────── */
.unit-chips-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 200px;
  overflow-y: auto;
}

.unit-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5px 8px;
  border-radius: 6px;
  border: 1px solid #374151;
  background: var(--table-row-bgcolor);
  cursor: pointer;
  font-size: 11px;
  transition: all 0.15s;
  min-width: 64px;
}
.unit-chip:hover  { border-color: #6b7280; }
.unit-chip.active { border-color: #10b981; background: #064e3b22; }
.unit-chip.dust   { border-style: dashed; border-color: #f59e0b88; }
.unit-chip.dust.active { border-color: #f59e0b; background: #78350f22; }

.chip-price { font-weight: 600; color: var(--text-color); }
.chip-qty   { font-size: 10px; color: var(--text-muted, #9ca3af); }

/* ── Add leg / send panel ────────────────────────────────────── */
.add-leg-btn {
  background: transparent;
  border: 1px dashed #4b5563;
  border-radius: 6px;
  color: var(--text-muted, #9ca3af);
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
  width: 100%;
  margin-top: 4px;
  transition: border-color 0.2s, color 0.2s;
}
.add-leg-btn:hover { border-color: #6366f1; color: #a5b4fc; }

.send-panel {
  border-top: 1px solid #374151;
  padding-top: 12px;
  margin-top: 6px;
}

.send-row {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.clear-btn {
  padding: 7px 16px;
  border-radius: 8px;
  border: 1px solid #4b5563;
  background: transparent;
  color: var(--text-muted, #9ca3af);
  font-size: 13px;
  cursor: pointer;
}
.clear-btn:hover { border-color: #6b7280; color: var(--text-color); }

.send-btn {
  padding: 7px 20px;
  border-radius: 8px;
  border: none;
  background: #10b981;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
}
.send-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.send-btn.full-width { width: 100%; margin-top: 8px; }

/* ── Validation errors ───────────────────────────────────────── */
.validation-errors {
  background: #fee2e2;
  padding: 8px 10px;
  border-radius: 6px;
  margin-bottom: 8px;
}
.validation-errors p { color: #b91c1c; font-size: 12px; margin: 2px 0; }

/* ── Shared text helpers ─────────────────────────────────────── */
.col-title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.badge {
  background: #374151;
  color: #9ca3af;
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 10px;
}
.empty-msg  { font-size: 12px; color: var(--text-muted, #6b7280); padding: 8px 0; }
.centered   { text-align: center; padding: 40px 20px; }

/* ── Reconcile layout ────────────────────────────────────────── */
.reconcile-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mode-toggle { display: flex; gap: 8px; }

.mode-btn {
  padding: 6px 16px;
  border-radius: 8px;
  border: 1px solid #4b5563;
  background: var(--table-row-bgcolor);
  color: var(--text-muted, #9ca3af);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}
.mode-btn.active {
  border-color: #6366f1;
  background: #1e1e2e;
  color: #a5b4fc;
  font-weight: 600;
}

.reconcile-grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 12px;
}

.cards-scroll {
  max-height: 400px;
  overflow-y: auto;
}

.reconcile-info {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 12px;
  margin-bottom: 10px;
  padding: 6px 8px;
  background: #1e1e2e;
  border-radius: 6px;
}

.chips-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 280px;
  overflow-y: auto;
  margin-bottom: 12px;
}

.spot-chip {
  background: var(--table-row-bgcolor);
  border-radius: 6px;
  padding: 8px 10px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
}
.spot-chip:hover     { border-color: #6b7280; }
.spot-chip.allocated { border-color: #10b981; background: #064e3b22; }

.chip-id  { font-size: 12px; font-weight: 600; }
.chip-sub { font-size: 11px; color: var(--text-muted, #9ca3af); }

.chip-qty-input { margin-top: 4px; }

.qty-input {
  width: 140px;
  padding: 3px 6px;
  border-radius: 4px;
  border: 1px solid #10b981;
  background: var(--table-row-bgcolor);
  color: var(--text-color);
  font-size: 12px;
}

.params-row {
  display: grid;
  grid-template-columns: auto 1fr auto 1fr;
  gap: 4px 8px;
  align-items: center;
  margin: 12px 0;
}
.small-label { font-size: 12px; font-weight: 600; }

.pnl-preview {
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 10px;
}
.pnl-preview.positive { background: #064e3b44; color: #10b981; }
.pnl-preview.negative { background: #7f1d1d44; color: #ef4444; }

/* ── Responsive ──────────────────────────────────────────────── */
@media (max-width: 900px) {
  .mapping-layout   { grid-template-columns: 1fr; }
  .reconcile-grid   { grid-template-columns: 1fr; }
}
</style>
